import React, { useState, useRef } from 'react';
import { Button, Card, Image } from 'antd';
import { PlayCircleOutlined, ShopOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import AppLogo from "./../../assets/logo-text.png";
import { useAuth } from "./../AuthProvider";
import { useNavigate } from "react-router-dom";
import CustomLoader from "../Common/CustomLoader";
import PriceTag from "./../../assets/price-tag.png";
import AmazonBtnImg from './../../assets/amazon-icon.svg';
import WhatsappGreenImg from "./../../assets/whatsapp-green-ic.png";
import You3 from './You3';
import NewFooter from './NewFooter';
import SocialSidebar from './SocialSidebar';

interface VideoData {
  id: string;
  embedId: string;
  title: string;
}

const NewLanding1: React.FC = () => {
  const [isMainVideoPlaying, setIsMainVideoPlaying] = useState(false);
  const { session, profileData, loading } = useAuth();
  const navigate = useNavigate();

  const handleRouteForRole = () => {
    // console.log('handleRouteForRole user role=', profileData?.role)
    switch (profileData?.role) {
      case 'sales':
        navigate("/chat/sales", { replace: true });
        break;
      case 'support':
        navigate("/chat/support", { replace: true });
        break;
      case 'admin':
        navigate("/chat", { replace: true });
        break;
      case 'no_role':
        navigate("/chat/profile", { replace: true });
        break;
      default:
        navigate("/chat/profile", { replace: true });
        break;
    }
  }

  React.useEffect(() => {
    if (session && !loading) {
      handleRouteForRole()
    }
  }, [session, navigate, loading]);

  // Sample YouTube video IDs - replace with actual video IDs
  const mainVideoId = "NILuIhKYfBo"; // Replace with actual main video ID

  const handleMainVideoPlay = () => {
    setIsMainVideoPlaying(true);
  };

  const YouTubeEmbed: React.FC<{ videoId: string; isPlaying: boolean; onPlay: () => void; className?: string }> = ({
    videoId,
    isPlaying,
    onPlay,
    className = ""
  }) => {
    if (isPlaying) {
      return (
        <iframe
          className={`w-full h-full rounded-xl ${className}`}
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    return (
      <div className={`relative w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl overflow-hidden ${className}`}>
        <img
          src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
          alt="Video thumbnail"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={onPlay}
            id='main_vid_play_btn'
            className="bg-white rounded-full p-4 shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 group"
          >
            <PlayCircleOutlined className="text-4xl text-gray-700 group-hover:text-blue-600" />
          </button>
        </div>
      </div>
    );
  };

  const openAmazon = () => {
    const url = `${import.meta.env.VITE_AMAZON_URL}`;
    window.open(url, "_blank"); // Opens blogs in a new tab
  };

  const openBlogs = () => {
    const url = "https://blog.spotgenie.in/";
    window.open(url, "_blank"); // Opens blogs in a new tab
  };

  const openWhatsApp = () => {
    const message = "Hello, I'm interested in SpotGenie!!!";
    const encodedMessage = encodeURIComponent(message);
    const url = `https://wa.me/${import.meta.env.VITE_WHATSAPP_OFFICIAL_NUMBER
      }?text=${encodedMessage}`;
    window.open(url, "_blank"); // Opens WhatsApp in a new tab
  };

  return (
    <>
      {loading && <CustomLoader />}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Header */}
        <header className="flex justify-between items-center px-4 md:px-6 lg:px-8 py-4 max-w-7xl mx-auto">
          <div className="flex items-center space-x-2 md:space-x-3">
            <Image
              src={AppLogo}
              className='cursor-pointer md:!h-9 !h-9'
              preview={false}
              onClick={() =>
                (window.location.href = `${import.meta.env.VITE_WEB_URL}`)
              }
            />
            {/* <span className="font-bold text-lg md:text-xl lg:text-2xl text-gray-800">SpotGenie</span>
            <span className="hidden sm:block text-xs md:text-sm lg:text-base text-gray-500">UPCOMING EVENTS</span> */}
          </div>
          <div className="flex items-center md:space-x-3 lg:space-x-4 !bg-[#EDF1F7] md:px-4 md:!h-16 !h-14 rounded-lg">
            <Button
              type='text'
              className='flex justify-center items-center !border-r-0'
              onClick={openBlogs}
              id="blogs_click">
              <span className="text-gray-600 hover:text-gray-800 cursor-pointer transition-colors font-ibmcondensedregular !text-[17px]">Blogs</span>
            </Button>
            <Button
              type='text'
              id="connect_whatsapp"
              className='flex justify-center items-center !border-r-0'
              onClick={openWhatsApp}>
              <span className="block text-gray-600 hover:text-gray-800 cursor-pointer transition-colors font-ibmcondensedregular !text-[17px]">Join us</span>
              <img src={WhatsappGreenImg} alt='WhatsApp' className='w-5 h-5' />
            </Button>
            <Button
              type='primary'
              size='large'
              id="order_now_amazon"
              // height={60}
              // preview={false}
              // src={AmazonBtnImg}
              onClick={openAmazon}
              className='md:!flex landing-amazon-btn !cursor-pointer !hidden items-center !border-r-0 justify-center !px-10 !h-12'
            >
              <span className='!text-[17px] font-ibmcondensedbold'>
                Order Now on Amazon
              </span>
              <img src={AmazonBtnImg} alt='Amazon Buy Button' className='w-5 h-5' />
            </Button>
          </div>
        </header>
        <SocialSidebar />
        {/* Hero Section */}
        <section className="text-center pt-20 pb-8 md:py-24 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto relative">
          {/* Price Tag */}
          <div className="!absolute !mr-0 md:!right-15 lg:!right-30 xl:!right-10 md:top-10 top-0 right-0">
            <Image
              src={PriceTag}
              className='md:!h-44 !h-28'
              preview={false}
            />
          </div>

          <h1 className="hidden md:block text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-gray-800 mb-4 md:mb-5 lg:mb-6 leading-tight px-2 md:px-4 font-ibmregular">
            Anyone can e-challan your vahan.
            <br />
            Just <em className="italic font-ibmitalic">One Click</em> Away.
          </h1>
          <h1 className="md:hidden block text-[28px] md:text-4xl lg:text-5xl xl:text-6xl font-light text-gray-800 mb-4 md:mb-5 lg:mb-6 leading-tight px-2 md:px-4 font-ibmregular">
            Anyone can e-challan your vahan. Just <em className="italic font-ibmitalic">One Click</em> Away.
          </h1>

          <p className="hidden md:block text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-6 md:mb-7 lg:mb-8 md:max-w-4xl mx-auto font-ibmregular">
            India has issued over <span className="font-ibmcondensedbold">18 crore e-challans</span> between 2019–2024, totaling ₹12,632 cr. That’s nearly <span className="font-ibmcondensedbold">1.3 lakh challans every day</span> across the country — and growing. <span className="font-ibmcondensedbold">SpotGenie</span> ensures they reach you first, not your car.
          </p>
          <p className="md:hidden block text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-6 md:mb-7 lg:mb-8 md:max-w-4xl mx-auto font-ibmregular">
            India has issued over <span className="font-ibmcondensedbold">18 crore e-challans</span> between 2019–2024, totaling ₹12,632 cr. That’s nearly <span className="font-ibmcondensedbold">1.3 lakh challans every day</span> across the country — and growing. <span className="font-ibmcondensedbold">SpotGenie</span> ensures they reach you first, not your car.
          </p>
          <div className='flex justify-center'>
            <Button
              type='primary'
              size='large'
              id="order_now_amazon"
              // height={60}
              // preview={false}
              // src={AmazonBtnImg}
              onClick={openAmazon}
              className='md:!flex landing-amazon-btn !cursor-pointer !hidden items-center !border-r-0 justify-center !px-10 !h-16'
            >
              <span className='!text-[17px] font-ibmcondensedbold'>
                Order Now on Amazon
              </span>
              <img src={AmazonBtnImg} alt='Amazon Buy Button' className='w-5 h-5' />
            </Button>
          </div>
        </section>

        {/* Main Video Section */}
        <section className="px-4 md:px-6 lg:px-8 mb-16 md:mb-28">
          <div className="max-w-7xl mx-auto">
            <div className="bg-blue-50 rounded-xl shadow-lg p-4 md:p-6 lg:p-8 xl:p-10">
              <div className="aspect-video rounded-xl overflow-hidden">
                <YouTubeEmbed
                  videoId={mainVideoId}
                  isPlaying={isMainVideoPlaying}
                  onPlay={handleMainVideoPlay}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="w-full bg-[#EDF1F7]">
          <div className='text-center pb-8 pt-16 md:pt-24 md:pb-5 px-4 md:px-6 lg:px-8 max-w-5xl mx-auto'>
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-gray-800 mb-4 md:mb-5 lg:mb-6 font-ibmregular">
              How SpotGenie Works — Simple, Fast, Effective.
            </h2>

            <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-6 md:mb-7 lg:mb-8 px-4 max-w-3xl mx-auto font-ibmregular">
              From registration to instant contact via WhatsApp — here’s how SpotGenie keeps you safe and reachable, before fines hit.
            </p>
          </div>
        </section>

        {/* YouTube Shorts Carousel */}
        <section className="px-4 md:px-6 lg:px-8 pb-16 md:pb-24 bg-[#EDF1F7]">
          <div className="max-w-7xl mx-auto">
            <You3 />
          </div>
        </section>
        <NewFooter />
      </div>
      <div className="!bg-white !fixed !bottom-0 !left-0 !right-0 !w-full flex justify-center items-center md:!hidden z-11">
        <Button
          type='primary'
          size='large'
          id="order_now_amazon"
          onClick={openAmazon}
          className='landing-amazon-btn  flex items-center justify-center w-full !h-15 !my-4 !mx-5'
        >
          <span className='font-ibmcondensedbold !text-[17px]'>
            Order Now On Amazon
          </span>
          <img src={AmazonBtnImg} alt='Amazon Buy Button' className='w-5 h-5' />
        </Button>
      </div>
    </>
  );
};

export default NewLanding1;